// components/pc/review/ReviewDetail.tsx
import styled from 'styled-components';
import ReviewMetaTag from '@/components/mo/Review/ReviewMetaTag';
import TagBadge from '@/components/mo/Review/TagBadge';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchReviewDetail } from '@/api/review';
import NewsAttachIcon from '@/assets/icon/news-attach.svg?react';
import NewsKakaoIcon from '@/assets/icon/news-kakao.svg?react';

const ReviewDetail = () => {
  const { id } = useParams<{ id: string }>();
  const {
    data: review,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['reviewDetail', id],
    queryFn: () => fetchReviewDetail(Number(id)),
    enabled: !!id,
  });

  if (isLoading) return <div>로딩 중...</div>;
  if (isError || !review) return <div>에러 발생</div>;

  return (
    <>
      <PageWrapper>
        <Container>
          <Header>
            <DateText>
              {review.incidentDate ? new Date(review.incidentDate).toLocaleDateString('ko-KR') : ''}
            </DateText>
            <Title>{review.title}</Title>
          </Header>
          <Divider />
          <ContentWrapper>
            <ContentInner>
              <MetaRow>
                <ReviewMetaTag
                  label={review.issues[0]?.isPayoutApproved ? '지급' : '부지급'}
                  totalFee={review.issues[0]?.totalFee?.toString()}
                />
              </MetaRow>

              <ContentTextBlock>{review.content}</ContentTextBlock>

              <TagRow>
                {review.tags.map((tag) => (
                  <TagBadge key={tag.id} label={`#${tag.tag}`} />
                ))}
              </TagRow>
            </ContentInner>
          </ContentWrapper>

          <SummaryWrapper>
            <SummaryInner>
              <SummaryLabel>손해사정사 총평</SummaryLabel>
              <SummaryTitle>{review.issues[0]?.issueTitle}</SummaryTitle>
              <SummaryBox>
                <AdjusterInfo>
                  {review.issues?.[0]?.adjuster?.profileImage ? (
                    <img
                      src={review.issues[0].adjuster.profileImage}
                      alt="손해사정사 프로필"
                      style={{
                        width: 55,
                        height: 55,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        objectPosition: 'top',
                      }}
                    />
                  ) : (
                    <AdjusterAvatar />
                  )}
                  <AdjusterMeta>
                    <AdjusterName>{review.issues[0]?.adjuster?.name} 손해사정사</AdjusterName>
                    <AdjusterTags>
                      {review?.issues?.[0]?.tags?.map((tag, index) => (
                        <TagPill key={index}>{tag}</TagPill>
                      ))}
                    </AdjusterTags>
                  </AdjusterMeta>
                </AdjusterInfo>
              </SummaryBox>
              <SummaryContent>{review.issues[0]?.issueContent}</SummaryContent>
            </SummaryInner>
          </SummaryWrapper>
        </Container>
      </PageWrapper>
      <IconWrapper>
        <NewsKakaoIcon
          style={{ cursor: 'pointer' }}
          onClick={() =>
            window.Kakao?.Link?.sendDefault({
              objectType: 'feed',
              content: {
                title: review.title,
                description: review.content.replace(/<[^>]+>/g, '').slice(0, 100),
                imageUrl: 'https://yourdomain.com/default-thumbnail.png',
                link: {
                  mobileWebUrl: window.location.href,
                  webUrl: window.location.href,
                },
              },
              buttons: [
                {
                  title: '웹으로 보기',
                  link: {
                    mobileWebUrl: window.location.href,
                    webUrl: window.location.href,
                  },
                },
              ],
            })
          }
        />
        <NewsAttachIcon
          style={{ cursor: 'pointer' }}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(window.location.href);
              alert('링크가 복사되었습니다.');
            } catch (err) {
              console.error('복사 실패:', err);
            }
          }}
        />
      </IconWrapper>
    </>
  );
};

export default ReviewDetail;

// Styled Components

const PageWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 80px 0; // 원하는 상하 여백
  box-sizing: border-box;
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  max-width: 630px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 0 16px; // ✅ 양쪽 여백 추가
  box-sizing: border-box;
`;

const Header = styled.div`
  align-self: stretch;
  text-align: center;
`;

const DateText = styled.div`
  color: var(--gray-ui_gray02, #252525);
  font-family: 'Spoqa Han Sans Neo';
  font-size: 18px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.36px;
`;

const Title = styled.div`
  color: var(--gray-ui_gray02, #252525);
  font-family: 'Spoqa Han Sans Neo';
  font-size: 32px;
  font-weight: 700;
  line-height: 130%;
  letter-spacing: -0.64px;
  margin-top: 8px;
`;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: center; // ✅ 가운데 정렬
  width: 100%;
  padding: 50px 0;
`;

const ContentInner = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const ContentTextBlock = styled.div`
  display: flex;
  padding: 16px 0;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;

  color: var(--gray-ui_gray02, #252525);
  font-family: 'Spoqa Han Sans Neo';
  font-size: 16px;
  font-weight: 400;
  line-height: 180%;
  letter-spacing: 1%;
  text-align: start;
`;

const TagRow = styled.div`
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  align-self: center;
`;

const SummaryWrapper = styled.div`
  display: flex;
  justify-content: center; // ✅ 가운데 정렬
  width: 100%;
`;

const SummaryInner = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 630px;
  align-items: center;
  padding: 0 16px;
  box-sizing: border-box;
`;

const SummaryLabel = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  color: var(--gray-ui_gray03, #3a3a3a);
  text-overflow: ellipsis;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 16px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.32px;
  opacity: 0.8;
  align-self: stretch;
  text-align: start;
  margin-bottom: 8px;
`;

const SummaryTitle = styled.div`
  align-self: stretch;
  color: var(--gray-ui_gray02, #252525);
  font-family: 'Spoqa Han Sans Neo';
  font-size: 32px;
  font-weight: 700;
  line-height: 140%;
  letter-spacing: -0.64px;
  text-align: start;
  margin-bottom: 24px;
`;

const SummaryBox = styled.div`
  display: flex;
  padding: 16px 30px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-radius: 16px;
  background: var(--table-BG-table_bg02_hov, #f0f3fe);
  margin-bottom: 50px;
`;

const AdjusterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const AdjusterAvatar = styled.div`
  display: flex;
  width: 55px;
  aspect-ratio: 1 / 1;
  padding: 5.6px;
  justify-content: center;
  align-items: center;
  border-radius: 181.5px;
  background: #fff;
  /* ✅ 여기에 프로필 이미지 넣어도 됨 */
`;

const AdjusterMeta = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
`;

const AdjusterName = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  color: var(--gray-ui_gray03, #3a3a3a);
  text-overflow: ellipsis;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 18px;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.36px;
`;

const AdjusterTags = styled.div`
  display: flex;
  fle
  flex-wrap: wrap; // 줄바꿈 허용 (원하는 경우)
  align-items: center;
  gap: 4px;
`;

const TagPill = styled.div`
  display: flex;
  padding: 4px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  background: var(--Primary-white, #fff);
  color: var(--gray-ui_gray04, #6f6f6f);
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.28px;
`;

const SummaryContent = styled.div`
  display: flex;
  padding: 16px 0;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;

  color: var(--gray-ui_gray03, #3a3a3a);
  font-family: 'Spoqa Han Sans Neo';
  font-size: 16px;
  font-weight: 400;
  line-height: 180%;
  letter-spacing: -0.32px;
  text-align: start;
`;

const Divider = styled.div`
  width: 100vw;
  text-align: center;
  border-top: 1px solid #000;
  max-width: 1240px;
`;

const IconWrapper = styled.div`
  display: inline-flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  padding-bottom: 70px;
`;
